import { useNavigate } from 'react-router-dom';
import { Notification } from '../../services/notificationService';
import { useNotifications } from '../../hooks/useNotifications';
import notificationAnalytics from '../../services/notificationAnalytics';
import { useAuth } from '../../context/AuthContext';
import { 
  Bell, 
  UserPlus, 
  Users, 
  Calendar, 
  CheckSquare, 
  Sparkles, 
  MessageSquare, 
  ClipboardList, 
  Activity,
  Award,
  Info,
  CreditCard
} from 'lucide-react';

interface NotificationItemProps {
  notification: Notification;
  onClose: () => void;
  compact?: boolean;
}

function getNotificationIconConfig(type?: string) {
  switch (type) {
    case 'family_invitation':
      return {
        Icon: UserPlus,
        bgClass: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200/60 dark:border-blue-800/50',
      };
    case 'member_added':
    case 'member_promoted':
      return {
        Icon: Users,
        bgClass: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border-indigo-200/60 dark:border-indigo-800/50',
      };
    case 'activity_assigned':
    case 'activity_completed':
      return {
        Icon: Calendar,
        bgClass: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-800/50',
      };
    case 'checklist_assigned':
    case 'checklist_completed':
      return {
        Icon: CheckSquare,
        bgClass: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border-amber-200/60 dark:border-amber-800/50',
      };
    case 'task':
    case 'task_assigned':
      return {
        Icon: ClipboardList,
        bgClass: 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border-purple-200/60 dark:border-purple-800/50',
      };
    case 'plan_changed':
    case 'subscription':
      return {
        Icon: CreditCard,
        bgClass: 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border-rose-200/60 dark:border-rose-800/50',
      };
    case 'reward':
    case 'reward_redeemed':
      return {
        Icon: Award,
        bgClass: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border-amber-200/60 dark:border-amber-800/50',
      };
    case 'system_message':
      return {
        Icon: MessageSquare,
        bgClass: 'bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400 border-sky-200/60 dark:border-sky-800/50',
      };
    default:
      return {
        Icon: Bell,
        bgClass: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200/60 dark:border-blue-800/50',
      };
  }
}

export default function NotificationItem({ notification, onClose, compact = false }: NotificationItemProps) {
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();
  const { user } = useAuth();
  const { Icon, bgClass } = getNotificationIconConfig(notification.type);

  const handleClick = async () => {
    // Marcar como leída
    if (!notification.read && notification.id) {
      await markAsRead(notification.id);
    }

    // Track analytics: clicked
    if (notification.id && user?.uid && notification.link) {
      await notificationAnalytics.trackEvent(
        notification.id,
        user.uid,
        notification.type,
        'clicked'
      );
    }

    // Navegar si hay link
    if (notification.link) {
      // Si la notificación tiene memberId en metadata, establecerlo en localStorage
      if (notification.metadata?.memberId) {
        localStorage.setItem('active_member_id', notification.metadata.memberId);
        if (notification.metadata?.memberName) {
          localStorage.setItem('active_member_name', notification.metadata.memberName);
        }
      }
      
      navigate(notification.link);
      onClose();
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `Hace ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Hace ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `Hace ${days}d`;
    return date.toLocaleDateString('es-MX');
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left border transition-all duration-200 relative group active:scale-[0.99] shadow-xs hover:shadow-md ${
        compact
          ? 'p-3 rounded-xl sm:rounded-2xl'
          : 'p-4 sm:p-5 rounded-2xl sm:rounded-3xl'
      } ${
        compact
          ? (!notification.read
              ? 'bg-blue-50/80 hover:bg-blue-50 dark:bg-blue-950/40 dark:hover:bg-blue-950/60 border-blue-200 dark:border-blue-800/70 ring-1 ring-blue-500/15'
              : 'bg-gray-50/90 hover:bg-gray-100 dark:bg-gray-800/70 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700/80')
          : (!notification.read 
              ? 'bg-white dark:bg-gray-900 border-[#055590]/30 dark:border-blue-500/40 ring-1 ring-[#055590]/10 dark:ring-blue-500/20' 
              : 'bg-white/70 dark:bg-gray-900/50 border-gray-200/80 dark:border-gray-800/80 hover:bg-white dark:hover:bg-gray-900')
      }`}
    >
      <div className={`flex items-start ${compact ? 'gap-2.5 sm:gap-3' : 'gap-3.5 sm:gap-4'}`}>
        {/* Lucide Icon Badge */}
        <div className={`${
          compact
            ? 'w-9 h-9 rounded-xl'
            : 'w-10 h-10 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl'
        } border flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 duration-200 shadow-sm ${bgClass}`}>
          <Icon className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} stroke-[2.2]`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex items-center justify-between gap-2">
            <h4 className={`text-xs ${compact ? 'font-bold' : 'sm:text-sm font-extrabold'} tracking-tight ${
              !notification.read 
                ? 'text-gray-900 dark:text-white' 
                : 'text-gray-600 dark:text-gray-400'
            }`}>
              {notification.title}
            </h4>
            
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-[10px] sm:text-[11px] font-bold text-gray-400 dark:text-gray-500">
                {formatTime(notification.createdAt)}
              </span>
              {!notification.read && (
                <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-[#055590] dark:bg-blue-500 rounded-full shadow-[0_0_8px_rgba(5,85,144,0.6)] shrink-0"></span>
              )}
            </div>
          </div>

          <p className={`text-xs ${compact ? 'mt-0.5' : 'sm:text-sm mt-1'} leading-relaxed line-clamp-2 ${
            !notification.read ? 'text-gray-800 dark:text-gray-200 font-semibold' : 'text-gray-500 dark:text-gray-400 font-normal'
          }`}>
            {notification.message}
          </p>
        </div>
      </div>
    </button>
  );
}
